'use client';

import { useTranslations } from 'next-intl';
import { CacheKeys } from '@headless-workspace/config';
import { AsyncData, ResultEmpty, ResultFailure, ResultSuccess } from '@headless-workspace/core-domain';
import { CommonDI, UserRecommendationsRepositoryType } from '../../../DI';
import { RecommendationCarouselType, RecommendedProductsValue } from '../../../server';
import { RevalidationStrategy, useSwrResult } from '../hooks';
import { useAppLocale } from '../i18n';

const DEFAULT_ERROR_KEY = 'RECOMMENDED_PRODUCTS_ERROR';

type UseTargetedProductRecommendationsReturnType = AsyncData<RecommendedProductsValue | null>;

export const useTargetedProductRecommendations = (
    carouselId: string,
    homeSlotCarouselIds: string[],
): UseTargetedProductRecommendationsReturnType => {
    const t = useTranslations('Discover.Home.carousel');
    const locale = useAppLocale();

    // We are using the useSwrResult hook here because we call a Headless API route
    // We do not need all the session revalidation and previous state data logics from useSafeSWR
    const { data, isLoading, error } = useSwrResult(
        CacheKeys.UserRecommendations,
        () => CommonDI.get(UserRecommendationsRepositoryType).getRecommendations({ locale, pageId: 'homepage' }),
        // Specific validation policy that will allow manual revalidation with mutate
        { ...RevalidationStrategy.ColdDataPolicy, revalidateOnMount: false },
    );

    if (!data || data.type !== 'success') {
        if (error) {
            return {
                isLoading,
                result: ResultFailure(
                    DEFAULT_ERROR_KEY,
                    error instanceof Error ? error.message : t('error.productRecommendations'),
                ),
            };
        }

        return { isLoading, result: ResultEmpty() };
    }

    const getRecommendationResult = (isLoading: boolean, recommendation: RecommendedProductsValue | undefined) => {
        return {
            isLoading,
            result: recommendation ? ResultSuccess(recommendation) : ResultEmpty(),
        };
    };

    /*
    This code is a quick win aiming at dealing with different ids received between HomeSlots and Recommendations
    We should remove this once we have a unified id between HomeSlots and Recommendations for potion reco
    */

    const potionCarousel = data.data.find((item) => item.carouselType === RecommendationCarouselType.POTION);

    if (potionCarousel) {
        const recommendationCarouselIds = new Set(data.data.map((item) => item.id));
        // Find the unused id from the homeSlotCarouselIds array by comparing it with the recommendationCarouselIds set
        const unusedId = homeSlotCarouselIds.find((item) => !recommendationCarouselIds.has(item));
        // If the unusedId is the same as the current carouselId, return the potion carousel
        if (unusedId === carouselId) {
            return getRecommendationResult(isLoading, potionCarousel);
        }
    }

    // end of quick win code

    const recommendation = data.data.find((item) => item.id === carouselId);
    return getRecommendationResult(isLoading, recommendation);
};
